import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Mail, Phone, Scale, Shield, FileText, ArrowLeft } from 'lucide-react';

const LegalImprint = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Zurück</span>
          </button>
          <div className="flex items-center gap-3 mb-4">
            <Scale className="w-10 h-10" />
            <h1 className="text-4xl font-bold">Impressum</h1>
          </div>
          <p className="text-blue-100 text-lg">
            Rechtliche Informationen gemäß § 5 TMG und § 55 RStV
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Company Information */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <Building2 className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Angaben gemäß § 5 TMG</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Firmierung und Rechtsform</h3>
              <p className="text-gray-700 leading-relaxed">
                <strong>Grid Balance UG</strong><br />
                GridBalance
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Geschäftsanschrift</h3>
              <address className="not-italic text-gray-700 leading-relaxed">
                Rosenhügel 4a<br />
                79599 Wittlingen<br />
                Deutschland
              </address>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Handelsregister</h3>
              <p className="text-gray-700 leading-relaxed">
                Registergericht: Amtsgericht Freiburg im Breisgau<br />
                Registernummer: HRB 733275
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Umsatzsteuer-Identifikationsnummer</h3>
              <p className="text-gray-700 leading-relaxed">
                gemäß § 27a Umsatzsteuergesetz:<br />
                DE457256973
              </p>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <Phone className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Kontaktaufnahme</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-gray-600 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Telefon</h3>
                <a href="tel:+4915223152476" className="text-blue-600 hover:text-blue-700 transition-colors">
                  +49 152 23152476
                </a>
              </div>
            </div>

            <div className="flex items-start gap-3 pt-4 border-t border-gray-100">
              <Mail className="w-5 h-5 text-gray-600 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">E-Mail</h3>
                <a href="mailto:oliver.wagner@gridbalance.de" className="text-blue-600 hover:text-blue-700 transition-colors">
                  oliver.wagner@gridbalance.de
                </a>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-2">Geschäftszeiten</h3>
              <p className="text-gray-700 leading-relaxed">
                Montag bis Freitag: 09:00 - 17:00 Uhr<br />
                Samstag, Sonntag und Feiertage: geschlossen
              </p>
            </div>
          </div>
        </section>

        {/* Legal Representation */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <FileText className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Vertretungsberechtigte Person</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Geschäftsführung</h3>
              <p className="text-gray-700 leading-relaxed">
                Oliver Wagner (Geschäftsführer)
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV</h3>
              <p className="text-gray-700 leading-relaxed">
                Oliver Wagner<br />
                Grid Balance UG<br />
                Rosenhügel 4a<br />
                79599 Wittlingen
              </p>
            </div>
          </div>
        </section>

        {/* Content Liability */}
        <section className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <Scale className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Haftungsausschluss und rechtliche Hinweise</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Haftung für Inhalte</h3>
              <p className="text-gray-700 leading-relaxed">
                Die Inhalte dieser Plattform wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, 
                Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. 
                Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten 
                nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als 
                Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde 
                Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige 
                Tätigkeit hinweisen.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Haftung für externe Links</h3>
              <p className="text-gray-700 leading-relaxed">
                Diese Plattform enthält Links zu externen Websites Dritter, auf deren Inhalte wir 
                keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine 
                Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige 
                Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum 
                Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte 
                waren zum Zeitpunkt der Verlinkung nicht erkennbar.
              </p>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Urheberrecht und Leistungsschutzrechte</h3>
              <p className="text-gray-700 leading-relaxed">
                Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten 
                unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, 
                Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes 
                bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers. 
                Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen 
                Gebrauch gestattet. Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt 
                wurden, werden die Urheberrechte Dritter beachtet.
              </p>
            </div>
          </div>
        </section>

        {/* Data Protection Reference */}
        <section className="bg-blue-50 rounded-xl p-8 mb-6 border border-blue-100">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Datenschutz</h2>
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            Informationen zur Verarbeitung Ihrer personenbezogenen Daten und zu Ihren Rechten 
            nach der Datenschutz-Grundverordnung (DSGVO) finden Sie in unserer Datenschutzerklärung.
          </p>
          <a 
            href="/privacy-policy" 
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold transition-colors"
          >
            Zur Datenschutzerklärung
            <FileText className="w-4 h-4" />
          </a>
        </section>

        {/* Footer Note */}
        <div className="text-center text-sm text-gray-500 mt-12 pt-8 border-t border-gray-200">
          <p>Dieses Impressum wurde zuletzt aktualisiert am: Januar 2026</p>
          <p className="mt-2">© 2026 Grid Balance UG. Alle Rechte vorbehalten.</p>
        </div>

      </div>
    </div>
  );
};

export default LegalImprint;